import React from 'react';
import Chart from "react-apexcharts";
import { useNavigate } from 'react-router-dom';
import { DashboardKPIs } from '../services/dashboardService';
import { TbUsers, TbUserCheck, TbClock, TbStethoscope } from 'react-icons/tb';
import '../styles/AdminKPICards.css';

interface AdminKPICardsProps {
  kpis: DashboardKPIs;
  showMarketBreakdown: boolean;
}

const AdminKPICards: React.FC<AdminKPICardsProps> = ({ kpis }) => {
  const navigate = useNavigate();

  const handleCardClick = (label: string) => {
    switch (label) {
      case "Active on PrEP":
        navigate('/admin/patients?status=ACTIVE_ON_PREP');
        break;
      case "Pending Insurance":
        navigate('/admin/patients?status=PENDING');
        break;
      case "Ready for Telehealth":
        navigate('/admin/patients?status=READY_FOR_TELEHEALTH');
        break;
      case "Total Registered":
        navigate('/admin/patients');
        break;
      default:
        break;
    }
  };

  // Chart config builder
  const getChartConfig = (type: "bar" | "area", color: string, data: number[]) => ({
    options: {
      chart: {
        width: 80,
        height: 54,
        type: type,
        toolbar: { show: false },
        sparkline: { enabled: true },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "70%",
          borderRadius: 3,
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        curve: "smooth" as const,
        colors: [color]
      },
      xaxis: {
        labels: { show: false },
        axisTicks: { show: false },
        axisBorder: { show: false },
      },
      yaxis: { show: false },
      grid: { show: false },
      tooltip: { enabled: false },
      colors: [color],
      fill: {
        type: type === "bar" ? "solid" : "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.3,
          stops: [0, 90, 100]
        }
      },
    } as any,
    series: [
      {
        name: "Trend",
        data: data,
      },
    ],
  });

  const cards = [
    {
      label: "Total Registered",
      value: kpis.totalRegistered,
      icon: <TbUsers size={22} />,
      bgColor: "bg-blue",
      chart: getChartConfig("bar", "#2E37A4", [30, 45, 60, 40, 70, 50, 90])
    },
    {
      label: "Pending HIV",
      value: kpis.pendingBreakdown?.hivTest || 0,
      icon: <TbUserCheck size={22} />,
      bgColor: "bg-success",
      chart: getChartConfig("area", "#00B69B", [20, 30, 45, 40, 55, 60, 70])
    },
    {
      label: "Pending Insurance",
      value: kpis.pendingBreakdown?.insurance || kpis.pending,
      icon: <TbClock size={22} />,
      bgColor: "bg-warning",
      chart: getChartConfig("bar", "#FF955A", [40, 30, 50, 60, 40, 70, 45])
    },
    {
      label: "Ready for Telehealth",
      value: kpis.readyForTelehealth,
      icon: <TbStethoscope size={22} />,
      bgColor: "bg-info",
      chart: getChartConfig("area", "#00AEEF", [30, 40, 35, 45, 40, 50, 48])
    }
  ];

  return (
    <div className="admin-kpis">
      <div className="admin-kpi-grid">
        {cards.map((card, index) => (
          <div
            key={index}
            className="premium-kpi-card"
            onClick={() => handleCardClick(card.label)}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-header-flex">
              <span className={`kpi-icon-wrapper ${card.bgColor}`}>
                {card.icon}
              </span>
            </div>
            <div className="card-footer-flex">
              <div className="value-section">
                <p className="kpi-label-text">{card.label}</p>
                <h3 className="kpi-value-text">{card.value}</h3>
              </div>
              <div className="chart-section">
                <Chart
                  options={card.chart.options}
                  series={card.chart.series}
                  type={card.chart.options.chart.type}
                  width={80}
                  height={54}
                />
              </div>
            </div>
          </div>
        ))}

        {/* Bottom Row: Conversion Stats in 3 columns */}
        <div className="conversion-cards-row">
          {/* Refills */}
          <div className="premium-kpi-card conversion-card-wide">
            <div className="performance-header">
              <span className="performance-title">Refills</span>
            </div>
            <div className="performance-body-new">
              <div className="perf-value-new">{kpis.refills}</div>
              <div className="perf-label-new">Active Prescriptions</div>
            </div>

            <div className="conversion-right-stats">
              <div className="conversion-sparkline">
                <Chart
                  options={getChartConfig("area", "#2E37A4", [10, 25, 15, 30, 22, 45, 40]).options}
                  series={getChartConfig("area", "#2E37A4", [10, 25, 15, 30, 22, 45, 40]).series}
                  type="area"
                  width={120}
                  height={60}
                />
              </div>
            </div>
          </div>

          {/* Renewals */}
          <div className="premium-kpi-card conversion-card-wide">
            <div className="performance-header">
              <span className="performance-title">Renewals</span>
            </div>
            <div className="performance-body-new">
              <div className="perf-value-new">{kpis.renewalsDue}</div>
            </div>

            <div className="conversion-right-stats">
              <div className="conversion-sparkline">
                <Chart
                  options={getChartConfig("area", "#00B69B", [10, 15, 12, 18, 14, 22, 20]).options}
                  series={getChartConfig("area", "#00B69B", [10, 15, 12, 18, 14, 22, 20]).series}
                  type="area"
                  width={120}
                  height={60}
                />
              </div>
            </div>
          </div>

          {/* Active on PrEP */}
          <div className="premium-kpi-card conversion-card-wide">
            <div className="performance-header">
              <span className="performance-title">Active on PrEP</span>
            </div>
            <div className="performance-body-new">
              <div className="perf-value-new">{kpis.activeOnPrep}</div>
            </div>

            <div className="conversion-right-stats">
              <div className="conversion-sparkline">
                <Chart
                  options={getChartConfig("area", "#00B69B", [10, 20, 15, 25, 22, 35, 30]).options}
                  series={getChartConfig("area", "#00B69B", [10, 20, 15, 25, 22, 35, 30]).series}
                  type="area"
                  width={120}
                  height={60}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminKPICards;